/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Node, NodeEntry, PathElement, PathInfo, SharedLinkEntry } from '@alfresco/js-api';
import { AlfrescoViewerModule, DocumentListComponent } from '@alfresco/adf-content-services';
import {
  CustomEmptyContentTemplateDirective,
  DataColumnComponent,
  DataColumnListComponent,
  EmptyContentComponent,
  PaginationComponent,
  ViewerModule
} from '@alfresco/adf-core';
import { DocumentListPresetRef, DynamicColumnComponent } from '@alfresco/adf-extensions';
import {
  ContentApiService,
  ContextActionsDirective,
  InfoDrawerComponent,
  PageComponent,
  PageLayoutComponent,
  PaginationDirective,
  ToolbarComponent
} from '@alfresco/aca-shared';
import { DocumentListDirective } from '@alfresco/aca-content';
import { TranslatePipe } from '@ngx-translate/core';
import { AngularSplitModule, SplitGutterInteractionEvent } from 'angular-split';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommentColumnService } from '../../services/comment-column.service';

type VirtualView = 'favorites' | 'recent' | 'shared' | 'trashcan';

const VIEW_CONFIG: Record<
  VirtualView,
  { rootId: string; title: string; icon: string; emptyTitle: string; emptyText?: string; preset: 'favorites' | 'recent' | 'shared' | 'trashcan' }
> = {
  favorites: {
    rootId: '-favorites-',
    title: 'APP.BROWSE.FAVORITES.TITLE',
    icon: 'star_rate',
    emptyTitle: 'APP.BROWSE.FAVORITES.EMPTY_STATE.TITLE',
    emptyText: 'APP.BROWSE.FAVORITES.EMPTY_STATE.TEXT',
    preset: 'favorites'
  },
  recent: {
    rootId: '-recent-',
    title: 'APP.BROWSE.RECENT.TITLE',
    icon: 'access_time',
    emptyTitle: 'APP.BROWSE.RECENT.EMPTY_STATE.TITLE',
    emptyText: 'APP.BROWSE.RECENT.EMPTY_STATE.TEXT',
    preset: 'recent'
  },
  shared: {
    rootId: '-sharedlinks-',
    title: 'APP.BROWSE.SHARED.TITLE',
    icon: 'people',
    emptyTitle: 'APP.BROWSE.SHARED.EMPTY_STATE.TITLE',
    emptyText: 'APP.BROWSE.SHARED.EMPTY_STATE.TEXT',
    preset: 'shared'
  },
  trashcan: {
    rootId: '-trashcan-',
    title: 'APP.BROWSE.TRASHCAN.TITLE',
    icon: 'delete',
    emptyTitle: 'APP.BROWSE.TRASHCAN.EMPTY_STATE.TITLE',
    preset: 'trashcan'
  }
};

@Component({
  selector: 'alg-virtual-files',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    DocumentListDirective,
    ContextActionsDirective,
    PaginationComponent,
    PaginationDirective,
    InfoDrawerComponent,
    PageLayoutComponent,
    ToolbarComponent,
    EmptyContentComponent,
    DynamicColumnComponent,
    DocumentListComponent,
    DataColumnListComponent,
    DataColumnComponent,
    CustomEmptyContentTemplateDirective,
    AngularSplitModule,
    AlfrescoViewerModule,
    ViewerModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './alg-virtual-files.component.html',
  styleUrls: ['./alg-virtual-files.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AlgVirtualFilesComponent extends PageComponent implements OnInit, AfterViewInit {
  private readonly contentApi = inject(ContentApiService);
  readonly commentColumn = inject(CommentColumnService);

  view: VirtualView = 'recent';
  columns: DocumentListPresetRef[] = [];
  previewOpened = false;
  previewNodeId: string | null = null;
  previewSharedLinkId: string | null = null;
  previewFileName = '';
  paneSizes: number[] = [60, 40];

  get config() {
    return VIEW_CONFIG[this.view];
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.view = this.resolveView();
    this.columns = this.extensions.documentListPresets[this.config.preset] || [];

    const savedSizes = localStorage.getItem('algVirtualPaneSizes');
    if (savedSizes) {
      this.paneSizes = JSON.parse(savedSizes);
    }
  }

  ngAfterViewInit(): void {
    this.documentList.ready.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.commentColumn.hydrate(this.documentList.data.getRows());
    });
  }

  openNode(event: Event): void {
    const node = (event as CustomEvent).detail?.node as NodeEntry | SharedLinkEntry;
    if (!node?.entry) {
      return;
    }

    if ('isFolder' in node.entry && node.entry.isFolder) {
      this.navigateToFolder(node.entry as Node);
      return;
    }

    // Un élément archivé ne possède plus d'endpoint de contenu standard prévisualisable.
    if (this.view === 'trashcan') {
      return;
    }

    this.documentList.resetSelection();
    this.documentList.preselectNodes = [node as NodeEntry];
    this.documentList.onPreselectNodes();

    if (this.view === 'shared') {
      this.previewSharedLinkId = node.entry.id;
      this.previewNodeId = null;
    } else {
      this.previewNodeId = node.entry.id;
      this.previewSharedLinkId = null;
    }
    this.previewFileName = node.entry.name;
    this.previewOpened = true;

    if (window.innerWidth <= 700) {
      this.paneSizes = [0, 100];
    } else if (window.innerWidth <= 1600) {
      this.paneSizes = [40, 60];
    }
  }

  closePreview(): void {
    this.previewOpened = false;
    this.previewNodeId = null;
    this.previewSharedLinkId = null;
    this.previewFileName = '';
    this.documentList.resetSelection();
  }

  onDragEnd(event: SplitGutterInteractionEvent): void {
    this.paneSizes = event.sizes as number[];
    localStorage.setItem('algVirtualPaneSizes', JSON.stringify(event.sizes));
  }

  private resolveView(): VirtualView {
    const url = this.router.url;
    if (url.includes('/favorites')) {
      return 'favorites';
    }
    if (url.includes('/shared')) {
      return 'shared';
    }
    if (url.includes('/trashcan')) {
      return 'trashcan';
    }
    return 'recent';
  }

  private navigateToFolder(node: Node): void {
    this.contentApi.getNode(node.id).subscribe(({ entry }) => {
      const isSite = (path: PathInfo): boolean => path?.elements?.some(({ name }: PathElement) => name === 'Sites');
      void this.router.navigate([isSite(entry.path) ? '/libraries' : '/personal-files', node.id]);
    });
  }
}
